/**
 * CMS Template Utility Functions
 *
 * This file contains utility functions for handling template variables
 * in CMS content and processing links with environment-specific values.
 */

/**
 * Resolves template placeholders in text using provided values
 *
 * @param text - Text containing placeholders
 * @param values - Dictionary of values to replace placeholders
 * @returns Text with placeholders replaced with actual values
 *
 * @example
 * ```typescript
 * const text = "Hello {{user.name}}!";
 * const resolved = resolveTemplates(text, { 'user.name': 'John' });
 * // Result: "Hello John!"
 * ```
 */
export function resolveTemplates(text: string, values: Record<string, string>): string {
    if (!text) return '';

    let result = text;

    // Replace all placeholders with their values
    Object.entries(values).forEach(([key, value]) => {
        const placeholder = `{{${key}}}`;
        result = result.replace(new RegExp(placeholder, 'g'), value);
    });

    return result;
}

/**
 * Creates a template resolver function with provided values
 *
 * @param values - Dictionary of values to replace placeholders
 * @returns Function that resolves templates
 *
 * @example
 * ```typescript
 * const resolver = createTemplateResolver({
 *   'user.name': 'John',
 *   'studio': 'https://studio.example.com'
 * });
 * const resolved = resolver("Welcome, {{user.name}}!");
 * // Result: "Welcome, John!"
 * ```
 */
export function createTemplateResolver(values: Record<string, string>) {
    return (text: string) => resolveTemplates(text || '', values);
}

/**
 * Gets default template values for the studio application
 *
 * @param userName - User's name to display
 * @param studioUrl - Base URL for the studio application
 * @returns Object containing default template values
 */
export function getDefaultTemplateValues(
    userName: string = 'User',
    studioUrl: string = '',
): Record<string, string> {
    return {
        'user.name': userName,
        studio: studioUrl,
        portal: '', // Default for portal links
        currentDate: new Date().toLocaleDateString(),
    };
}

/**
 * Parses navigation links to handle special variables (legacy support)
 *
 * @param link - The original link string
 * @param studioUrl - The studio URL to replace {{studio}} placeholders
 * @returns Processed link with variables replaced
 */
export function parseNavigationLink(link: string, studioUrl: string = ''): string {
    if (!link) return '';

    return resolveTemplates(link, {
        studio: studioUrl,
        portal: '',
    });
}

/**
 * Process card data to replace template variables in specific fields
 *
 * @param card - The card data to process
 * @param templateValues - Values for template variables
 * @returns Processed card with templates resolved
 */
export function processCardTemplates(card: any, templateValues: Record<string, string>): any {
    if (!card) return card;

    const resolver = createTemplateResolver(templateValues);

    return {
        ...card,
        TitleText: resolver(card.TitleText),
        BodyText: card.BodyText ? resolver(card.BodyText) : card.BodyText,
        Link: card.Link ? resolver(card.Link) : card.Link,
    };
}

/**
 * Process banner data to replace template variables in specific fields
 *
 * @param banner - The banner data to process
 * @param templateValues - Values for template variables
 * @returns Processed banner with templates resolved
 */
export function processBannerTemplates(banner: any, templateValues: Record<string, string>): any {
    if (!banner) return banner;

    const resolver = createTemplateResolver(templateValues);

    return {
        ...banner,
        CTA: resolver(banner.CTA),
        Tagline: banner.Tagline ? resolver(banner.Tagline) : banner.Tagline,
        Action: banner.Action
            ? {
                  ...banner.Action,
                  Link: resolver(banner.Action.Link),
                  Text: resolver(banner.Action.Text),
              }
            : banner.Action,
    };
}

/**
 * Process title data to replace template variables in specific fields
 *
 * @param title - The title data to process
 * @param templateValues - Values for template variables
 * @returns Processed title with templates resolved
 */
export function processTitleTemplates(title: any, templateValues: Record<string, string>): any {
    if (!title) return title;

    const resolver = createTemplateResolver(templateValues);

    return {
        ...title,
        Title: resolver(title.Title),
        Subtitle: title.Subtitle ? resolver(title.Subtitle) : title.Subtitle,
    };
}

import { Card } from '@/@cms/types/entities';
import { SidebarItemType } from '@/@studio/types/constants';
import { SidebarItem } from '@/@studio/types/entities';
import { RouteAccessParams, shouldDisableRoute } from './routeAccess';

// Re-export all route access utilities for convenience
export * from './routeAccess';
export { filterCardByPermissions } from './card-permissions';
export type { FilterCardPermissionsOptions } from './card-permissions';

export function isPathMatch(itemPath: string, currentPath: string): boolean {
    const normalizedItemPath = itemPath.replace(/\/$/, '');
    const normalizedCurrentPath = currentPath.replace(/\/$/, '');
    return (
        normalizedCurrentPath === normalizedItemPath ||
        normalizedCurrentPath.startsWith(`${normalizedItemPath}/`)
    );
}

export function transformCardToSidebarItem(card: Card): SidebarItem {
    return {
        key: card.TitleText
            ? `${card.TitleText.toLowerCase().replace(/\s+/g, '-')}-${card.id}`
            : `card-${card.id}`,
        title: card.TitleText,
        icon: `lucide:${card.TitleIcon}`,
        href: card.Link,
        // 'AI' is deliberately not in this list: the Ask DANI card carries
        // Flag: 'AI' for its home-page tile badge, but the sidebar item
        // shows no badge. Same field, two surfaces — filtering here keeps
        // the tile's badge intact.
        flag: card.Flag === 'Beta' || card.Flag === 'Alpha' ? card.Flag : undefined,
        type: card.isNested ? SidebarItemType.Nest : undefined,
    };
}

/**
 * Determines if a card should be disabled based on user access and license status
 *
 * @param cardLink - The link/route of the card
 * @param params - User and org information for access checking
 * @returns true if the card should be disabled
 */
export function shouldDisableCard(cardLink: string, params?: RouteAccessParams): boolean {
    if (!cardLink) return false;

    // If no params provided, we can't determine access - don't disable
    if (!params) return false;

    return shouldDisableRoute(cardLink, params);
}
